import type { TemplateSpecView } from "@/types";

// #303 (UC-2, Konzept §8): EINE Formular-Mechanik für alle Vorgangs-Felder.
//
// Zwei Masken benutzen sie: die FREIGABE (#302 — der Mensch-Schritt liefert seine
// `produces`-Felder) und der MANUELLE START (#301 — die `input`-Felder des Auslösers).
// Beide fragen dasselbe („welche Felder, welcher Typ, welche sind Pflicht?"), und beide
// müssen dieselben Werte-Typen abliefern; zwei Umsetzungen wären zwei Chancen, die
// Typ-Prüfung des Masters unterschiedlich falsch zu treffen.
//
// PUR und ohne React testbar.

/** Die Feld-Definition, wie die Oberfläche sie braucht. Spiegel der Vorlagen-Spec (v1). */
export interface CasefieldDef {
  key: string;
  label: string;
  /** `string` | `text` | `number` | `money` | `date` | `boolean` | `choice`. */
  type: string;
  required: boolean;
  choices?: string[];
  /**
   * Die Definition war nicht auffindbar — das Feld wird als Text behandelt.
   *
   * ⚠ Das ist eine ANSAGE, kein Detail: der Master prüft die Werte gegen die GEPINNTE
   * Vorlage. Wer hier eine Zahl als Text abschickt, bekommt eine Ablehnung, und die
   * Oberfläche muss vorher gesagt haben, warum sie raten musste.
   */
  unbekannt?: boolean;
}

/** Formular-Zustand: rohe Eingabe-Strings je Feld-Key (Booleans als "true"/"false"). */
export type CasefieldWerte = Record<string, string>;

/**
 * Eine Feld-BESCHREIBUNG, wie sie der Master liefert — aus der Konfigurations-Sicht der
 * Einrichtung (`template-spec`) ODER, seit #303 Review H1, direkt an der offenen Freigabe
 * (`pendingApproval.feldDefs`). Zwei Quellen, EINE Form.
 */
export interface Feldbeschreibung {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  choices?: string[];
}

/**
 * Löst Feld-Keys gegen die Vorlagen-Spec auf.
 *
 * Fehlt die Spec (Einrichtung gelöscht, kein Leserecht, ältere gepinnte Version), entsteht
 * KEIN leeres Formular: die Felder werden als Text angeboten und mit `unbekannt` markiert.
 * Ein Entscheider, der nichts eingeben kann, wäre die schlechtere Antwort als einer, der
 * eingibt und im Zweifel eine klare Ablehnung bekommt.
 */
export function alsCasefieldDefs(
  keys: readonly string[],
  spec: TemplateSpecView | undefined,
  opts: { pflicht?: readonly string[] } = {},
): CasefieldDef[] {
  return ausBeschreibungen(keys, spec?.casefields, opts);
}

/**
 * Dieselbe Auflösung aus einer beliebigen Liste von Feld-Beschreibungen.
 *
 * ⚠ Es gibt sie, weil die FREIGABE ihre Beschreibungen nicht aus der Einrichtung holen
 * kann: der Entscheider ist nicht deren Inhaber, `GET …/template-spec` antwortet ihm 404,
 * und ohne Typ wäre jedes Feld ein Textfeld (ein `money`/`choice`-Gate also gar nicht
 * abschliessbar). Zwei Auflösungen wären zwei Chancen, die Typ-Prüfung des Masters
 * unterschiedlich falsch zu treffen — deshalb delegiert `alsCasefieldDefs` hierher.
 */
export function ausBeschreibungen(
  keys: readonly string[],
  beschreibungen: readonly Feldbeschreibung[] | undefined,
  opts: { pflicht?: readonly string[] } = {},
): CasefieldDef[] {
  const pflicht = opts.pflicht ? new Set(opts.pflicht) : null;
  const byKey = new Map((beschreibungen ?? []).map((f) => [f.key, f] as const));
  return keys.map((key) => {
    const f = byKey.get(key);
    if (!f) {
      return { key, label: key, type: "string", required: pflicht ? pflicht.has(key) : true, unbekannt: true };
    }
    return {
      key,
      label: f.label || key,
      type: f.type,
      // `pflicht` (der Start-Kontrakt `input.required`) schlägt das deklarative
      // Feld-`required`: es sind zwei Ebenen, und beim Start gilt der Kontrakt.
      required: pflicht ? pflicht.has(key) : f.required === true,
      ...(f.choices ? { choices: f.choices } : {}),
    };
  });
}

/** Vorbelegte Werte (Instanz-Defaults, Gate-Snapshot) in Formular-Strings übersetzen. */
export function vorbelegung(
  felder: readonly CasefieldDef[],
  werte: Record<string, unknown> | null | undefined,
): CasefieldWerte {
  const out: CasefieldWerte = {};
  for (const f of felder) {
    const v = werte?.[f.key];
    if (v === undefined || v === null) continue;
    out[f.key] = typeof v === "object" ? JSON.stringify(v) : String(v);
  }
  return out;
}

/** Welche Pflichtfelder sind (noch) leer? Gibt LABELS zurück — die stehen im Hinweis. */
export function fehlendePflichtfelder(
  felder: readonly CasefieldDef[],
  werte: CasefieldWerte,
): string[] {
  return felder.filter((f) => f.required && (werte[f.key] ?? "").trim() === "").map((f) => f.label);
}

const DATUM_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formular-Strings → typisierte Nutzlast.
 *
 * Leere Felder fallen WEG (ein leerer String ist keine Angabe — und für ein optionales
 * Textfeld wäre er eine falsche). Eine unlesbare Zahl oder ein unmögliches Datum ist ein
 * FEHLER mit Feld-Namen, nie ein stilles Weglassen: sonst schickt die Oberfläche
 * kommentarlos weniger ab, als der Mensch eingetippt hat (dieselbe Regel wie in der
 * Instanz-Konfiguration, #300 Review LOW-15).
 */
export function baueNutzlast(
  felder: readonly CasefieldDef[],
  werte: CasefieldWerte,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const f of felder) {
    const roh = (werte[f.key] ?? "").trim();
    if (roh === "") continue;
    switch (f.type) {
      case "number":
      case "money": {
        // Komma-Dezimaltrennung ist in der Schweiz üblich genug, um sie anzunehmen.
        const n = Number(roh.replace(",", "."));
        if (!Number.isFinite(n)) throw new Error(`„${f.label}" braucht eine Zahl`);
        out[f.key] = n;
        break;
      }
      case "boolean": {
        if (roh !== "true" && roh !== "false") throw new Error(`„${f.label}" braucht Ja oder Nein`);
        out[f.key] = roh === "true";
        break;
      }
      case "date": {
        // Der Master akzeptiert Kalendertag ODER vollen Zeitstempel mit Zone; das Formular
        // liefert immer den Kalendertag. Geprüft wird trotzdem, dass es einer IST — ein
        // getippter Wert aus einem Browser ohne Datums-Feld landet sonst als Text im 400.
        if (!DATUM_RE.test(roh) || Number.isNaN(Date.parse(roh))) {
          throw new Error(`„${f.label}" braucht ein Datum (JJJJ-MM-TT)`);
        }
        out[f.key] = roh;
        break;
      }
      case "choice": {
        if (f.choices && f.choices.length > 0 && !f.choices.includes(roh)) {
          throw new Error(`„${f.label}" braucht eine der vorgegebenen Antworten`);
        }
        out[f.key] = roh;
        break;
      }
      default:
        out[f.key] = roh;
    }
  }
  return out;
}

/**
 * #303 (Review L6): ein vorbelegtes Feld, das der Mensch GELEERT hat.
 *
 * `baueNutzlast` lässt leere Felder weg — und genau das macht das Leeren wirkungslos: der
 * Master legt die Vorbelegungen der Einrichtung UNTER die mitgeschickten Felder
 * (`{...inputDefaults, ...fields}`), der Vorschlag käme also zurück. Wer den leeren
 * Textwert wirklich meint, muss ihn deshalb ausdrücklich mitschicken.
 *
 * ⚠ Nur `string`/`text` lassen sich leeren: ein leerer Wert ist für Zahl, Datum, Ja/Nein
 * und Auswahl kein gültiger Wert (der Master lehnt ihn ab). Solche Felder kommen als
 * `nichtLeerbar` zurück — die Maske SAGT das, statt still den Vorschlag zurückzuholen.
 */
export function geleerteVorbelegungen(
  felder: readonly CasefieldDef[],
  werte: CasefieldWerte,
  defaults: Record<string, unknown> | undefined,
): { leer: Record<string, string>; nichtLeerbar: string[] } {
  const leer: Record<string, string> = {};
  const nichtLeerbar: string[] = [];
  for (const f of felder) {
    // PFLICHTFELDER bleiben aussen vor: ein geleertes Pflichtfeld sperrt den Start ohnehin
    // und wird dort schon benannt (`fehlendePflichtfelder`). Ein zweiter Hinweis daneben
    // („der Vorschlag gilt weiter") wäre Rauschen — und für das abgeschickte Formular
    // schlicht unwahr, weil es gar nicht abgeschickt werden kann.
    if (f.required) continue;
    const hatVorschlag = defaults !== undefined && defaults[f.key] !== undefined && defaults[f.key] !== null;
    if (!hatVorschlag) continue;
    if ((werte[f.key] ?? "").trim() !== "") continue;
    if (f.type === "string" || f.type === "text") leer[f.key] = "";
    else nichtLeerbar.push(f.label);
  }
  return { leer, nichtLeerbar };
}
